import { useState, useEffect } from "react";
import type {
  FlowStepViewModel,
  FlowConfigViewModel,
  LLMConfigOptionsViewModel,
  StepRuntimeOptionsValue,
  OCROptions,
} from "@models/doctype";
import { useFlowConfig } from "./useFlowConfig";
import { useModelCatalog } from "./useModelCatalog";
import { useAdminPermissions } from "@hooks/useAdminPermissions";
import type {
  FlowConfigFormData,
  LLMStepConfigFormData,
  OCRStepConfigFormData,
  AgentExecutionFormData,
} from "@utils/builders/flowConfig";
import { buildCreateFlowConfigRequest, buildFlowStepFormData } from "@utils/builders/flowConfig";
import { buildConfigOptionsFromForm } from "@utils/flowConfigHelpers";
import { parseJsonText } from "@utils/jsonText";

interface UseStepEditProps {
  open: boolean;
  step: FlowStepViewModel;
  flowConfig: FlowConfigViewModel;
  docTypeId: string;
  initialTab?: string;
}

export const useStepEdit = ({
  open,
  step,
  flowConfig,
  docTypeId,
  initialTab,
}: UseStepEditProps) => {
  const { createFlowConfig, isLoading, error: saveError, clearError } = useFlowConfig();
  const { canCreateDoctype } = useAdminPermissions();
  const catalog = useModelCatalog(step.ocrStepConfig ? "ocr" : "chat", open && canCreateDoctype);
  const [activeTab, setActiveTab] = useState<string>("0");
  const [hasChanges, setHasChanges] = useState(false);
  /** Provider key, exactly as the step stores it. */
  const [selectedProvider, setSelectedProvider] = useState<string>("");

  // LLM Config State
  const [modelName, setModelName] = useState("");
  const [systemPrompt, setSystemPrompt] = useState("");
  const [userPrompt, setUserPrompt] = useState("");
  const [temperature, setTemperature] = useState<number | string>("");
  const [topP, setTopP] = useState<number | string>("");
  const [topK, setTopK] = useState<number | string>("");
  const [maxOutputTokens, setMaxOutputTokens] = useState<number | string>("");
  const [thinkingBudget, setThinkingBudget] = useState<number | string>("");
  const [seed, setSeed] = useState<number | string>("");
  const [runtimeConfigOptions, setRuntimeConfigOptions] =
    useState<LLMConfigOptionsViewModel | null>(null);
  const [stepRuntimeOptions, setStepRuntimeOptions] =
    useState<StepRuntimeOptionsValue | null>(null);
  const [responseSchema, setResponseSchema] = useState("");

  // OCR Config State
  const [ocrModelName, setOcrModelName] = useState("");
  const [ocrOptions, setOcrOptions] = useState<OCROptions | null>(null);

  // Agent Executions State
  const [agents, setAgents] = useState<AgentExecutionFormData[]>([]);

  const hasAgents = Array.isArray(step.llmStepConfig?.executions);

  useEffect(() => {
    if (open && initialTab !== undefined) {
      setActiveTab(initialTab);
    } else if (open) {
      setActiveTab("0");
    }
  }, [open, initialTab]);

  useEffect(() => {
    const initializeDialog = () => {
      if (!open) return;
        if (step.llmStepConfig) {
          setSelectedProvider(step.llmStepConfig.provider || "");
          setModelName(step.llmStepConfig.modelName);
          setSystemPrompt(step.llmStepConfig.systemPrompt);
          setUserPrompt(step.llmStepConfig.userPrompt);

          const options = step.llmStepConfig.configOptions;
          setRuntimeConfigOptions(options ? { ...options } : null);
          setStepRuntimeOptions(
            Object.fromEntries(
              Object.entries({
                fallbackModel: step.llmStepConfig.fallbackModel,
                chunkingOptions: step.llmStepConfig.chunkingOptions,
                pageDetectionPolicy: step.llmStepConfig.pageDetectionPolicy,
              }).filter(([, value]) => value !== undefined && value !== null),
            ),
          );
          setTemperature(options?.temperature?.toString() || "");
          setTopP(options?.topP?.toString() || "");
          setTopK(options?.topK?.toString() || "");
          setMaxOutputTokens(options?.maxOutputTokens?.toString() || "");
          setThinkingBudget(options?.thinkingBudget?.toString() || "");
          setSeed(options?.seed?.toString() || "");

          setResponseSchema(
            step.llmStepConfig.responseSchema
              ? JSON.stringify(step.llmStepConfig.responseSchema, null, 2)
              : "",
          );

          if (step.llmStepConfig.provider && canCreateDoctype) {
            void catalog.loadModels(step.llmStepConfig.provider);
          }

          // The agents are edited on their own; a step save keeps them as they are.
          setAgents(buildFlowStepFormData(step).llmStepConfig?.executions ?? []);
        }
        if (step.ocrStepConfig) {
          setSelectedProvider(step.ocrStepConfig.provider || "");
          setOcrModelName(step.ocrStepConfig.modelName);
          setOcrOptions(step.ocrStepConfig.ocrOptions || null);
          if (step.ocrStepConfig.provider && canCreateDoctype) {
            void catalog.loadModels(step.ocrStepConfig.provider);
          }
        }
        setHasChanges(false);
        clearError();
    };

    initializeDialog();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, open]);

  const handleProviderChange = (providerKey: string) => {
    if (!providerKey || providerKey === selectedProvider) return;
    setSelectedProvider(providerKey);
    // A model name belongs to its provider: changing the provider clears it.
    setModelName("");
    setOcrModelName("");
    markAsChanged();
    void catalog.loadModels(providerKey);
  };

  const handleModelChange = (name: string) => {
    if (step.llmStepConfig) {
      setModelName(name);
    } else if (step.ocrStepConfig) {
      setOcrModelName(name);
    }
    markAsChanged();
  };

  const handleAgentsChange = (newAgents: AgentExecutionFormData[]) => {
    setAgents(newAgents);
    markAsChanged();
  };

  const markAsChanged = () => {
    if (!hasChanges) setHasChanges(true);
  };

  const schema = parseJsonText(responseSchema);
  /** A multi-agent step: its agents carry the user prompt and schema, the step's are unused. */
  const isMultiAgent = (step.llmStepConfig?.executions?.length ?? 0) > 0;
  /** What blocks saving, per editor tab; empty when the step can be saved. */
  const validation = {
    model: step.llmStepConfig ? !modelName.trim() : !!step.ocrStepConfig && !ocrModelName.trim(),
    systemPrompt: !!step.llmStepConfig && !systemPrompt.trim(),
    userPrompt: !!step.llmStepConfig && !isMultiAgent && !userPrompt.trim(),
    responseSchema: schema.ok ? null : schema.error,
  };
  const isValid = !validation.model && !validation.systemPrompt && !validation.userPrompt && !validation.responseSchema;

  const handleSave = async () => {
    if (!isValid || !schema.ok) return null;
    const providerKey =
      selectedProvider ||
      step.llmStepConfig?.provider ||
      step.ocrStepConfig?.provider ||
      "";

    const updatedSteps = flowConfig.steps.map((s) => {
      if (s.stepOrder === step.stepOrder) {
        if (step.llmStepConfig) {
          const llmConfig: LLMStepConfigFormData = {
            provider: providerKey,
            modelPrefix: step.llmStepConfig.modelPrefix,
            modelName,
            systemPrompt,
            userPrompt,
            configOptions: buildConfigOptionsFromForm(
              { temperature, topP, topK, maxOutputTokens, thinkingBudget, seed },
              runtimeConfigOptions,
            ),
            responseSchema: schema.value,
            executions: agents.length > 0 ? agents : null,
            fallbackModel: stepRuntimeOptions?.fallbackModel,
            chunkingOptions: stepRuntimeOptions?.chunkingOptions,
            pageDetectionPolicy: stepRuntimeOptions?.pageDetectionPolicy,
          };

          return {
            stepOrder: s.stepOrder,
            stepType: s.stepType,
            llmStepConfig: llmConfig,
          };
        }

        if (step.ocrStepConfig) {
          const ocrConfig: OCRStepConfigFormData = {
            provider: providerKey,
            modelPrefix: step.ocrStepConfig.modelPrefix,
            modelName: ocrModelName,
            ocrOptions,
          };

          return {
            stepOrder: s.stepOrder,
            stepType: s.stepType,
            ocrStepConfig: ocrConfig,
          };
        }
      }

      return buildFlowStepFormData(s);
    });

    const formData: FlowConfigFormData = {
      flowBaseId: flowConfig.flowBaseId,
      flowType: flowConfig.flowType,
      flowDescription: flowConfig.flowDescription,
      steps: updatedSteps,
      preprocessingOptions: flowConfig.preprocessingOptions,
    };

    const request = buildCreateFlowConfigRequest(formData);
    const result = await createFlowConfig(docTypeId, request);

    if (result) {
      setHasChanges(false);
    }

    return result;
  };

  return {
    activeTab,
    hasChanges,
    providers: catalog.providers,
    models: catalog.modelsFor(selectedProvider),
    selectedProvider,
    isLoadingProviders: catalog.isLoadingProviders,
    isLoadingModels: catalog.isLoadingModels,
    isLoading,
    saveError,
    clearError,
    validation,
    isValid,
    isMultiAgent,
    modelName,
    systemPrompt,
    userPrompt,
    temperature,
    topP,
    topK,
    maxOutputTokens,
    thinkingBudget,
    seed,
    runtimeConfigOptions,
    stepRuntimeOptions,
    responseSchema,
    ocrModelName,
    ocrOptions,
    // Agent executions
    hasAgents,
    agents,
    setActiveTab,
    setSystemPrompt,
    setUserPrompt,
    setTemperature,
    setTopP,
    setTopK,
    setMaxOutputTokens,
    setThinkingBudget,
    setSeed,
    setRuntimeConfigOptions,
    setStepRuntimeOptions,
    setResponseSchema,
    setOcrOptions,
    handleProviderChange,
    handleModelChange,
    handleSave,
    markAsChanged,
    handleAgentsChange,
  };
};
